/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import { Injectable, Inject, OnDestroy } from '@angular/core';
import { WorkPanelManagerService, WorkPanelStatus, WorkPanelType } from './work-panel-manager.service';
import { Subject } from 'rxjs';
import { first, take, takeUntil } from 'rxjs/operators';
import { FeatureToggleService, GlobalEventBusService, IDeviceModel, TicketContextService, hd } from '@nx-fe/services';
import { GlobalEvents } from 'app/core/global-event-constants';
import { BroadcastService, BroadcastChannels } from 'app/utils/broadcastService';
import { RemoteControlService } from './remote-control.service';
import { IWorkPanelInformation } from 'app/modules/ticket/components/work-panel/work-panel-rc2.models';
import { ISnapshotServiceState, SnapshotService } from 'app/modules/ticket/services/snapshot.service';
import { ISnapshotInformation } from 'app/modules/ticket/components/snapshot/snapshot/snapshot.models';
import { Router } from '@angular/router';
import { TicketPageService } from 'app/modules/ticket/ticket-page.service';
import { CrossWindowEvents, CrossWindowProxy } from 'app/modules/remote-control-ng/crossWindowProxy';
import { CrossWindowMessage } from 'app/modules/remote-control-ng/definitions';
import { XsI18nPipe } from '@nx-fe/pipes';
import { IRemoteControlTransferData } from './remote-control.interfaces';
import { DeviceStateRegistryService, RemoteControlDeviceState } from './device-state-registry.service';

export interface IWorkPanelWindow extends Window {
    rcContext: any;
    [key: string]: any;
    workPanelTransferData: IWorkPanelTransferData;
}

export interface IWorkPanelTransferData {
    workPanelKey: string;
    workPanelService: WorkPanelService;
    workPanelManagerService: WorkPanelManagerService;
    snapshotServiceState: ISnapshotServiceState;
    remoteControlTransferData: IRemoteControlTransferData;
    remoteControlService: RemoteControlService;
    ticketContextService: TicketContextService;
    broadcastSessionId: string;
    onClose: () => void;
}

@Injectable({
    providedIn: 'root'
})
export class WorkPanelService implements OnDestroy {

    private workPanelTransferData: IWorkPanelTransferData = null;
    private popoutWindow: IWorkPanelWindow;
    private _unbindOnWindowMessageHandler: any = null;
    private _locationServiceOnChangeUnbind: Function;
    private _onTicketLoadedUnbind: Function;
    private readonly popoutRemoteControlStop: BroadcastService<IDeviceModel> = new BroadcastService<IDeviceModel>(BroadcastChannels.onPopoutRemoteControlStop);
    private readonly popoutRemoteControlStopped: BroadcastService<void> = new BroadcastService<void>(BroadcastChannels.onPopoutRemoteControlStopped);
    private readonly workPanelPopout: BroadcastService<boolean> = new BroadcastService<boolean>(BroadcastChannels.onWorkPanelPopout);

    private onDestroy$ = new Subject<void>();
    private $window: any = window;

    constructor(
        private uiRouter: Router,
        private workPanelManagerService: WorkPanelManagerService,
        private snapshotService: SnapshotService,
        private globalEventBusService: GlobalEventBusService,
        private remoteControlService: RemoteControlService,
        private deviceStateRegistry: DeviceStateRegistryService,
        private ticketContextService: TicketContextService,
        private ticketPageService: TicketPageService,
        private featureToggleService: FeatureToggleService,
        private saI18nPipe: XsI18nPipe,
    ) { }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();

        if (this.popoutRemoteControlStop) {
            this.popoutRemoteControlStop.close();
        }

        if (this.popoutRemoteControlStopped) {
            this.popoutRemoteControlStopped.close();
        }

        if (this.workPanelPopout) {
            this.workPanelPopout.close();
        }

        if (this._locationServiceOnChangeUnbind) {
            this._locationServiceOnChangeUnbind();
        }
    }

    public set workPanelKey(value: string) {
        if (this.$window.workPanelTransferData) {
            this.$window.workPanelTransferData.workPanelKey = value;
        }

        if (this.workPanelTransferData) {
            this.workPanelTransferData.workPanelKey = value;
        }
    }

    public popOut = (workPanelKey: string) => {
        if (!workPanelKey) {
            return;
        }

        const device = this.workPanelManagerService.getPanelDevice(workPanelKey);
        if (!device) {
            return;
        }

        if (!this._unbindOnWindowMessageHandler) {
            this._unbindOnWindowMessageHandler = CrossWindowProxy.onWindowMessage(this.$window, this.onWindowMessageHandler);
        }
        // console.debug('Registered onWindowMessage onWindowMessageHandler');

        this.createWorkPanelTransferData(device, workPanelKey);

        const panelType = this.workPanelManagerService.getPanelType(workPanelKey);

        if (this.deviceStateRegistry.isRemoteControlConnected(device.deviceId)) {
            this.workPanelManagerService.changePanelStatus(workPanelKey, WorkPanelStatus.poppedOut);
            this.popOutRc(workPanelKey, device.deviceId);
        } else if (panelType == WorkPanelType.liveSnapshot) {
            this.popOutLiveSnapshot(workPanelKey, device.deviceId);
        }
    };

    public closePopOutWindow = () => {
        if (this.popoutWindow && this.popoutWindow.close) {
            this.workPanelManagerService.removeActiveWorkPanelsByStatus(WorkPanelStatus.poppedOut);
            this.popoutWindow.close();
            if (this._locationServiceOnChangeUnbind) {
                this._locationServiceOnChangeUnbind();
            }
        }
    };

    /** Starts popping in a panel.
     * Runs in the context of the popped out window */
    public popIn = () => {
        const mainWindow = this.$window.opener as IWorkPanelWindow;
        const workPanelKey = this.$window.workPanelTransferData.workPanelKey;
        const device = this.workPanelManagerService.getPanelDevice(workPanelKey);

        if (mainWindow) {
            mainWindow.workPanelTransferData = this.$window.workPanelTransferData;

            if (mainWindow.workPanelTransferData.remoteControlTransferData
                && mainWindow.workPanelTransferData.remoteControlTransferData.remoteControlService.deviceStateRegistry.isRemoteControlConnected(device.deviceId)) {
                const deviceState = this.deviceStateRegistry.getState(device.deviceId);
                deviceState.popoutWindow = this.$window as any;
                this.remoteControlService.popin(deviceState, mainWindow as any);

            } else {
                CrossWindowProxy.postMessage(mainWindow, CrossWindowEvents.WORK_PANEL_POPIN);

            }
        }
    };

    public completePopIn = () => {
        this.popoutWindow.onbeforeunload = null;

        // as the popin is complete unsubscribe the popout window unload monitoring before that
        // window is closed, as the monitor would end the RC session
        this.remoteControlService.unsubscribePopoutCloseMonitoring(this.popoutWindow);

        if (this.popoutWindow && this.popoutWindow.close && !this.popoutWindow.closed) {
            this.popoutWindow.close();
        }
    };

    /** Called from standalone, to initiate embeding into ticket page */
    public embedIntoTicket = (workPanelKey: string) => {

        if (!workPanelKey) {
            return;
        }

        const device = this.workPanelManagerService.getPanelDevice(workPanelKey);
        if (!device) {
            return;
        }

        // First create the workpanel transfer data
        this.createWorkPanelTransferData(device, workPanelKey);

        // Now add the remote control transfer data
        const deviceState = this.deviceStateRegistry.getState(device.deviceId);

        if (this.deviceStateRegistry.isRemoteControlConnected(device.deviceId)) {

            this.workPanelTransferData.remoteControlTransferData = this.remoteControlService.createRemoteControlTransferData(deviceState);
        }

        // Set it to the window object so it get loaded from there after transfer
        this.$window.workPanelTransferData = this.workPanelTransferData;

        const panelType = this.workPanelManagerService.getPanelType(workPanelKey);

        // Mark the current panel as embedded
        this.workPanelManagerService.changePanelStatus(workPanelKey, WorkPanelStatus.embedded);

        // If there is a remote connection, then tell the remote control service to do its embedding magic
        if (this.deviceStateRegistry.isRemoteControlConnected(device.deviceId)) {
            this.embedRemoteControlIntoTicket(workPanelKey, device.deviceId);
        }

        this.ticketPageService.TicketLoaded.pipe(first()).subscribe(ticket => {
            setTimeout(() => {
                this.finishEmbeddingIntoTicket(deviceState, panelType);
            });
        });
        // This code will run once the ticket is loaded, so we can finish embedding into the ticket what ever we where doing in standalone
        // const ticketLoadedSub = this.globalEventBusService.register(GlobalEvents.ticketLoaded, () => {

        //     // We are done with this event handler, so we can unsubscribe
        //     ticketLoadedSub.unsubscribe();
        //     setTimeout(()=>{
        //         this.finishEmbeddingIntoTicket(deviceState, panelType);
        //     }, 5000);
        // });
    };

    /** Called from ticket page, to finalize embedding from stand alone */
    private finishEmbeddingIntoTicket = (deviceState: RemoteControlDeviceState, panelType: WorkPanelType) => {

        this.remoteControlService.receiveRemoteControlTransfer(deviceState)
            .then(() => {

                if (panelType === WorkPanelType.liveSnapshot) {
                    const snapshotInfo: ISnapshotInformation = {
                        device: deviceState.device,
                        mcConnectionName: deviceState.device.$$mcConnectionName,
                        snapshotActivity: null,
                        workPanelKey: null
                    };
                    this.globalEventBusService.broadcast(GlobalEvents.openLiveSnapshot, snapshotInfo);
                } else {
                    this.globalEventBusService.broadcast(GlobalEvents.openRcWorkPanel, { device: deviceState.device, workPanelKey: null } as IWorkPanelInformation);
                }

                // Clear the already transfered data from the window object
                if (this.$window.workPanelTransferData) {
                    // this.$window.workPanelTransferData.remoteControlTransferData = null;
                    this.$window.workPanelTransferData = null;
                }
            });
    };

    private embedRemoteControlIntoTicket = (workPanelKey: string, deviceId: string) => {

        if (this.featureToggleService.isEnabled('DeveloperMode')) { console.log('Embedding RC into ticket page..'); }

        const deviceState = this.deviceStateRegistry.getState(deviceId);

        this.remoteControlService.embedIntoTicket(deviceState);
    };

    public transferDeviceState = (panelState: IDeviceModel, fromService: WorkPanelService): IDeviceModel => {
        this.snapshotService.setSnapshotServiceState(fromService.workPanelTransferData.snapshotServiceState);
        return panelState;
    };

    public isRcDisabled = (currentWorkPanelKey: string): boolean => {
        const currentDevice = this.workPanelManagerService.getPanelDevice(currentWorkPanelKey);
        if (!currentDevice) {
            return true;
        }
        return !this.deviceStateRegistry.canRemoteControl(currentDevice as hd.IDeviceModel)
    };

    public isRCAvailable = (currentWorkPanelKey: string): boolean => {
        const currentDevice = this.workPanelManagerService.getPanelDevice(currentWorkPanelKey);
        if (!currentDevice) {
            return false;
        }
        const devicePermissions = this.deviceStateRegistry.getPermissions(currentDevice.deviceId);
        return (
          devicePermissions &&
          devicePermissions.permissions &&
          (devicePermissions.permissions & hd.RemoteControlPermissions.RemoteControl) !== 0
        );
    };

    public getRCToolTip = (deviceState: RemoteControlDeviceState, currentWorkPanelKey: string): string => {
        let isRemoteViewOnly = false;
        if (deviceState && deviceState.device) {
            isRemoteViewOnly = this.deviceStateRegistry.isRemoteViewOnly(deviceState.device);
        }
        const device = this.workPanelManagerService.getPanelDevice(currentWorkPanelKey);
        if (device) {
            isRemoteViewOnly = this.deviceStateRegistry.isRemoteViewOnly(device as hd.IDeviceModel);
        }
        let tooltip = isRemoteViewOnly
            ? this.saI18nPipe.transform('deviceCard_RemoteViewButton_Caption')
            : this.saI18nPipe.transform('ticket_RemoteControlTabs_Remote');
        if (this.isRcDisabled(currentWorkPanelKey)) {
            tooltip = tooltip + ' ' + this.saI18nPipe.transform('deviceCard_RemoteControl_not_available');
        }
        return tooltip;
    };

    private popOutRc = (workPanelKey: string, deviceId: string) => {
        const deviceState = this.deviceStateRegistry.getState(deviceId);
        if (!this.remoteControlService.popOutValidate(deviceState.device)) {
            return;
        }

        this.workPanelManagerService.changePanelStatus(workPanelKey, WorkPanelStatus.poppedOut);

        this.remoteControlService.popoutOnWindow(deviceState.device, () => {
            this.popoutWindow = this.createWindow();

            return this.popoutWindow;
        });
    };

    private popOutLiveSnapshot = (workPanelKey: string, deviceId: string) => {
        if (!this.canPopOut(deviceId)) {
            return;
        }

        this.workPanelManagerService.changePanelStatus(workPanelKey, WorkPanelStatus.poppedOut);
        this.workPanelPopout.postMessage(true);
        this.popoutWindow = this.createWindow();
    };

    private canPopOut(deviceId: string) {
        return true;
    }

    private createWindow = () => {
        const url = 'workPanelPopout';

        //XTODO
        // this._locationServiceOnChangeUnbind = this.uiRouter..onChange(() => { // nagivate away from ticket page
        //     this.closePopOutWindow();
        // })
        const left = 0; const top = 0; const width = 800; const height = 600;
        const popoutWindow = this.$window.open(url, '_blank', 'scrollbars=yes, resizable=yes, width=' + width + ', height=' + height + ', top=' + top + ', left=' + left) as IWorkPanelWindow;

        popoutWindow.onbeforeunload = () => {
            // when we click X on window
            this.closePopOutWindow();
        };

        // Maybe not the best place to set this, but currently we need a reference
        // to the popup window in the remotecontrolservice so when starting RC from
        // the popout, the remote control service can transfer the data to the popup
        this.remoteControlService.popoutWindow = popoutWindow as IWorkPanelWindow;

        return popoutWindow;
    };

    private createWorkPanelTransferData = (device: IDeviceModel, workPanelKey: string) => {
        this.$window.addEventListener('unload', (e) => {
            if (this.popoutWindow && !this.popoutWindow.closed) {
                this.popoutWindow.close();
                this.popoutWindow = null;
                this._locationServiceOnChangeUnbind();
            }
        });

        this.workPanelTransferData = {
            workPanelKey,
            workPanelService: this,
            workPanelManagerService: this.workPanelManagerService,
            snapshotServiceState: this.snapshotService.getSnapshotServiceState(),
            remoteControlTransferData: null,
            remoteControlService: this.remoteControlService,
            ticketContextService: this.ticketContextService,
            broadcastSessionId: BroadcastService.sessionId,
            onClose: () => {
                this.popoutRemoteControlStopped.postMessage();
            }
        };

        this.remoteControlService.remoteControlEnded$.pipe(
            takeUntil(this.onDestroy$),
            take(1),
        ).subscribe(() => {
            this.popoutRemoteControlStopped.postMessage();
        });
    };

    private onWindowMessageHandler = (msg: CrossWindowMessage) => {
        // console.debug(`Receiving postMessage in onWindowMessageHandler ${msg.type} on ${this.$window?.location.href}`, msg);
        switch (msg.type) {
            case CrossWindowEvents.WORK_PANEL_POPOUT_READY: {
                const workPanelKey = this.workPanelTransferData.workPanelKey;
                const panelType = this.workPanelManagerService.getPanelType(workPanelKey);

                switch (panelType) {
                    case WorkPanelType.liveSnapshot:
                    case WorkPanelType.rc: {
                        this.popoutWindow.workPanelTransferData = this.workPanelTransferData;
                        if (this.remoteControlService.remoteControlPopoutData) {

                            this.workPanelTransferData.remoteControlTransferData = this.remoteControlService.remoteControlPopoutData;
                        }
                        break;
                    }
                }

                break;
            }
            case CrossWindowEvents.WORK_PANEL_POPIN:
            case CrossWindowEvents.RC_POPIN: {
                // Runs on the Main window context when we are popping in to it.
                const workPanelKey = this.popoutWindow.workPanelTransferData.workPanelKey;
                const device = this.workPanelManagerService.getPanelDevice(workPanelKey);
                const panelType = this.workPanelManagerService.getPanelType(workPanelKey);

                if (this._unbindOnWindowMessageHandler) {
                    this._unbindOnWindowMessageHandler();
                    this._unbindOnWindowMessageHandler = null;
                }

                this.workPanelManagerService.changePanelStatus(workPanelKey, WorkPanelStatus.embedded);

                this.workPanelManagerService.workPanels = this.popoutWindow.workPanelTransferData.workPanelManagerService.workPanels;
                const openWorkPanelEvent = this.getOpenWorkPanelEvent(panelType);
                this.globalEventBusService.broadcast(openWorkPanelEvent, { device, workPanelKey } as IWorkPanelInformation);

                if (this.popoutWindow.workPanelTransferData.remoteControlTransferData
                    && this.popoutWindow.workPanelTransferData.remoteControlTransferData.remoteControlService.deviceStateRegistry.isRemoteControlConnected(device.deviceId)) {

                    this.remoteControlService.completePopin(this.popoutWindow);
                }
                else {
                    this.completePopIn();
                }

                break;
            }
            case CrossWindowEvents.WORK_PANEL_ATTACH_SCREENSHOT:
                if (msg.data.fileObject) {
                    msg.data.fileObject.name = msg.data.name;
                }
                this.globalEventBusService.broadcast(GlobalEvents.attachScreenshot, msg.data);
                break;
        }
    };

    private getOpenWorkPanelEvent(panel: WorkPanelType): string {
        switch (panel) {
            case WorkPanelType.liveSnapshot:
                return GlobalEvents.openLiveSnapshot;
            case WorkPanelType.rc:
                return GlobalEvents.openRcWorkPanel;
            default:
                return null;
        }
    }
}
